package fr.frux.feature.treedetail

import fr.frux.core.designsystem.components.harvestPeriodLabel
import fr.frux.core.designsystem.components.untilEndOfLabel
import fr.frux.core.designsystem.icons.glyphFor
import fr.frux.core.designsystem.theme.FruxStatus
import fr.frux.core.designsystem.theme.label
import fr.frux.feature.treedetail.resources.Res
import fr.frux.feature.treedetail.resources.treedetail_imported_bordeaux
import fr.frux.feature.treedetail.resources.treedetail_imported_grenoble
import fr.frux.feature.treedetail.resources.treedetail_imported_lozere
import fr.frux.feature.treedetail.resources.treedetail_imported_lyon
import fr.frux.feature.treedetail.resources.treedetail_imported_metz
import fr.frux.feature.treedetail.resources.treedetail_imported_montpellier
import fr.frux.feature.treedetail.resources.treedetail_imported_nantes
import fr.frux.feature.treedetail.resources.treedetail_imported_osm
import fr.frux.feature.treedetail.resources.treedetail_imported_paris
import fr.frux.feature.treedetail.resources.treedetail_imported_plaine_commune
import fr.frux.feature.treedetail.resources.treedetail_imported_rennes
import fr.frux.feature.treedetail.resources.treedetail_imported_strasbourg
import fr.frux.feature.treedetail.resources.treedetail_imported_toulouse
import fr.frux.shared.domain.model.Certainty
import fr.frux.shared.domain.model.HarvestWindow
import fr.frux.shared.domain.model.LookalikeSeverity
import fr.frux.shared.domain.model.RawEdibility
import fr.frux.shared.domain.model.SpeciesLookalike
import fr.frux.shared.domain.model.SpeciesPhoto
import fr.frux.shared.domain.model.SystemAccounts
import fr.frux.shared.domain.model.TreeTrait
import fr.frux.shared.domain.model.TreeTraitKind
import fr.frux.shared.domain.model.months
import fr.frux.shared.domain.model.soonMonths
import fr.frux.shared.presentation.isFieldStatusStale
import fr.frux.shared.presentation.resolveTreeStatus
import fr.frux.shared.presentation.treeSighting
import org.jetbrains.compose.resources.StringResource
import kotlin.time.Clock
import kotlin.time.ExperimentalTime
import kotlin.time.Instant
import fr.frux.shared.domain.model.Tree as DomainTree

/**
 * Converts Domain Tree model to TreeDetail UI model.
 *
 * [harvests] et [lookalikes] viennent du référentiel espèces (vides si le référentiel est
 * indisponible ou si l'espèce n'a rien de déclaré) ; [currentMonth] est le mois courant, injecté
 * par l'appelant pour rester testable sans horloge système.
 */
@OptIn(ExperimentalTime::class)
fun DomainTree.toTreeDetail(
    harvests: List<HarvestWindow>,
    currentMonth: Int,
    lookalikes: List<SpeciesLookalike> = emptyList(),
    // Photo d'illustration de l'espèce (#389), résolue en cascade côté serveur. Ne s'affiche que si
    // l'arbre n'a aucune photo réelle : la règle d'effacement vit ici, pas dans l'écran.
    speciesPhoto: SpeciesPhoto? = null,
    // Snapshot de l'utilisateur courant (#129 lot B) : gouverne "Signaler un doute" (masqué sur son
    // propre arbre) et "Maintenir mon identification" (créateur seul). Null pour un visiteur anonyme.
    currentUserId: Long? = null,
    // Horloge injectée (#420), jamais Clock.System.now() en dur : la dérivation du passage humain
    // doit rester pilotable depuis les tests. Défaut système pour ne pas alourdir les 30+ appels
    // existants de ce mapper, qui ne portent pas sur cette dérivation.
    now: Instant = Clock.System.now(),
): TreeDetail {
    val inSeasonMonths = harvests.flatMap { it.months() }.toSet()
    val soonMonthsSet = harvests.soonMonths()
    val resolvedStatus = resolveTreeStatus(status, harvests, currentMonth, lastObservedAt, now)
    // Galerie dans l'ordre du serveur (createdAt DESC, #223 lot A/D) : la première est le héro.
    // Les deux droits se calculent photo par photo (#247) — on signale celle d'un autre, on
    // supprime la sienne, jamais l'inverse, et un visiteur anonyme ne fait ni l'un ni l'autre.
    val galleryPhotos =
        photos.map { photo ->
            TreeDetailPhoto(
                id = photo.id,
                url = photo.thumbUrl,
                fullUrl = photo.fullUrl,
                canDelete = currentUserId != null && photo.authorId == currentUserId,
                // Visible pour l'anonyme (#264) : le tap poste l'invite de connexion côté VM plutôt
                // que d'être masqué — seule la suppression reste une vraie permission de propriétaire.
                canReport = photo.authorId != currentUserId,
            )
        }
    return TreeDetail(
        id = id,
        speciesName = species.commonNameFr,
        speciesId = species.id,
        scientificName = species.scientificName,
        status = resolvedStatus,
        photoUrl = photoUrl,
        speciesGlyph = glyphFor(species.scientificName, harvestPart?.name),
        speciesPhoto = speciesPhoto.takeIf { photos.isEmpty() && photoUrl == null },
        description = description,
        latitude = latitude,
        longitude = longitude,
        createdBy = createdBy,
        // Vrai pseudo renvoyé par le backend (#55) ; null si absent → l'UI affiche un libellé neutre,
        // jamais un identifiant maquillé en nom.
        creatorUsername = createdByUsername,
        createdAt = createdAt,
        // Les deux chips sont toujours visibles : un kind sans trait posé retombe à count 0.
        traits =
            TreeTraitKind.entries.map { kind ->
                traits.firstOrNull { it.kind == kind } ?: TreeTrait(kind, 0, mine = false)
            },
        latestObservation = latestObservation,
        myObservationStatus = myObservationStatus,
        // Un seul vocabulaire pour le statut : le libellé du design system, plus de jeu de mots
        // divergent ("Parfait" etc.) porté localement.
        ripenessLabel = resolvedStatus.label,
        certaintyLabel =
            when (certainty) {
                Certainty.CERTAIN -> "Identification certaine"
                Certainty.PROBABLE -> "Identification probable"
                Certainty.TO_CONFIRM -> "Identification à confirmer"
            },
        certainty = certainty,
        inSeasonMonths = inSeasonMonths,
        soonMonths = soonMonthsSet,
        currentMonth = currentMonth,
        seasonLabel = seasonLabel(harvests, currentMonth),
        hazard = resolveHazard(lookalikes, certainty, disputed),
        preparationLabel = resolvePreparationLabel(harvests),
        disputed = disputed,
        disputedLabel = resolveDisputedLabel(disputedKinds),
        reportedByMe = reportedByMe,
        reportedGoneByMe = reportedGoneByMe,
        isOwnTree = currentUserId != null && createdBy == currentUserId,
        canModerateCreator =
            createdByUsername != null &&
                !(currentUserId != null && createdBy == currentUserId) &&
                !SystemAccounts.isImport(createdByUsername),
        importSource = ImportedTreeSource.fromUsername(createdByUsername),
        photos = galleryPhotos,
        sighting = treeSighting(lastObservedAt, photoUrl, now),
        lastObservedAtMillis = lastObservedAt?.toEpochMilliseconds(),
        sightingNowMillis = now.toEpochMilliseconds(),
        statusStale = isFieldStatusStale(status, harvests, currentMonth, lastObservedAt, now),
        lastCorrection = lastCorrection,
    )
}

/**
 * Origine d'un arbre importé en masse, reconnue par le pseudo réservé de son compte système
 * (`V55`, `V83`, `V84` et `V85`, un compte par source). Le contrat public n'expose pas `external_source` : le pseudo
 * suffit à reconnaître l'origine sans élargir l'API. Couplage assumé entre le pseudo et le
 * libellé affiché, borné à ces treize valeurs (ADR 0010 amendé le 2026-10-01, #501).
 */
enum class ImportedTreeSource(
    val username: String,
    val url: String,
    /** Tout le jeu est en bord de route (#722) : la fiche affiche une mise en garde d'accès. */
    val roadside: Boolean = false,
) {
    OSM(username = "openstreetmap", url = "https://www.openstreetmap.org/copyright"),
    RENNES(
        username = "rennes-metropole",
        url = "https://data.rennesmetropole.fr/explore/dataset/arbre/",
    ),
    MONTPELLIER(
        username = "montpellier-metropole",
        url = "https://data.montpellier3m.fr/dataset/arbres-dalignement-de-montpellier",
    ),
    BORDEAUX(
        username = "bordeaux-metropole",
        url = "https://datahub.bordeaux-metropole.fr/explore/dataset/ec_arbre_p/",
    ),
    LYON(
        username = "metropole-de-lyon",
        url = "https://data.grandlyon.com/portail/fr/jeux-de-donnees/arbres-alignement-metropole-lyon/info",
    ),
    STRASBOURG(
        username = "eurometropole-de-strasbourg",
        url = "https://data.strasbourg.eu/explore/dataset/patrimoine_arbore/",
    ),
    NANTES(
        username = "nantes-metropole",
        url = "https://data.nantesmetropole.fr/explore/dataset/244400404_patrimoine-arbore-nantes-metropole/",
    ),
    TOULOUSE(
        username = "toulouse-metropole",
        url = "https://data.toulouse-metropole.fr/explore/dataset/arbres-urbains/",
    ),
    PLAINE_COMMUNE(
        username = "plaine-commune",
        url = "https://www.data.gouv.fr/datasets/les-arbres-geres-par-plaine-commune",
    ),
    PARIS(
        username = "ville-de-paris",
        url = "https://opendata.paris.fr/explore/dataset/les-arbres/",
    ),
    GRENOBLE(
        username = "grenoble-alpes-metropole",
        url = "https://www.data.gouv.fr/datasets/patrimoine-arbore-du-territoire-metropolitain",
    ),
    METZ(
        username = "eurometropole-de-metz",
        url = "https://www.data.gouv.fr/datasets/arbres-urbains-eurometropole-de-metz",
    ),
    LOZERE(
        username = "departement-de-la-lozere",
        url = "https://www.data.gouv.fr/datasets/arbres-dalignement",
        roadside = true,
    ),
    ;

    companion object {
        fun fromUsername(username: String?): ImportedTreeSource? = entries.firstOrNull { it.username == username }
    }
}

/** Libellé d'en-tête « Importé de <source> » (#501) : treize comptes système, treize libellés. */
internal fun importSourceLabel(source: ImportedTreeSource): StringResource =
    when (source) {
        ImportedTreeSource.OSM -> Res.string.treedetail_imported_osm
        ImportedTreeSource.RENNES -> Res.string.treedetail_imported_rennes
        ImportedTreeSource.MONTPELLIER -> Res.string.treedetail_imported_montpellier
        ImportedTreeSource.BORDEAUX -> Res.string.treedetail_imported_bordeaux
        ImportedTreeSource.LYON -> Res.string.treedetail_imported_lyon
        ImportedTreeSource.STRASBOURG -> Res.string.treedetail_imported_strasbourg
        ImportedTreeSource.NANTES -> Res.string.treedetail_imported_nantes
        ImportedTreeSource.TOULOUSE -> Res.string.treedetail_imported_toulouse
        ImportedTreeSource.PLAINE_COMMUNE -> Res.string.treedetail_imported_plaine_commune
        ImportedTreeSource.PARIS -> Res.string.treedetail_imported_paris
        ImportedTreeSource.GRENOBLE -> Res.string.treedetail_imported_grenoble
        ImportedTreeSource.METZ -> Res.string.treedetail_imported_metz
        ImportedTreeSource.LOZERE -> Res.string.treedetail_imported_lozere
    }

/** Libellé générique du bandeau contesté : repli quand le détail des kinds manque (#208). */
internal const val DISPUTED_GENERIC_LABEL = "Identification contestée par des cueilleurs"

/**
 * Libellé du bandeau contesté selon les kinds de signalements ouverts (#208, ADR 0015) : un
 * signalement GONE seul parle de disparition, pas d'un doute d'identification. Un kind inconnu
 * du client (contrat ouvert, cf. `TreeReportRequest.kind`) ou une liste vide (cache hors ligne)
 * retombent sur le libellé générique.
 */
internal fun resolveDisputedLabel(disputedKinds: List<String>): String {
    val misidentified = "MISIDENTIFIED" in disputedKinds
    val gone = "GONE" in disputedKinds
    return when {
        misidentified && gone -> "Identification contestée et disparition signalée par des cueilleurs"
        gone -> "Signalé comme disparu par des cueilleurs"
        else -> DISPUTED_GENERIC_LABEL
    }
}

/**
 * #426 : le bandeau de signalement (`resolveDisputedLabel`) et la ligne de passage humain
 * ([treeSighting]) se contredisaient à l'écran sur un arbre signalé disparu (« Personne n'est
 * encore passé devant cet arbre » sous « Signalé comme disparu »). La date du signalement n'est
 * pas disponible côté client (`tree_reports` n'expose pas sa date dans le contrat public,
 * ADR 0025 : un seul écrivain pour `last_observed_at`) : on ne peut donc pas faire porter cette
 * date à la dérivation de présentation. Le bandeau de signalement dit déjà l'essentiel, la ligne
 * de passage n'ajoute rien tant que l'arbre est contesté.
 */
internal fun shouldShowSightingLine(disputed: Boolean): Boolean = !disputed

/**
 * Alerte sosie (ADR 0015) : le bandeau rouge (sosie `LETHAL` × identification non sûre) prime sur
 * la ligne repliée « ne pas confondre ». La consigne de préparation est résolue à part
 * ([resolvePreparationLabel]) : c'est un fait culinaire de la partie récoltée, pas un niveau
 * d'alerte — le déroulé sureau de l'ADR les affiche ensemble.
 *
 * [disputed] (#129 lot B) élargit la même condition que `certainty != CERTAIN` : un arbre contesté
 * par les pairs est une identification non sûre au même titre qu'une déclaration `PROBABLE`/
 * `TO_CONFIRM` — l'ADR l'exprime comme « étends sa condition à `|| disputed` ».
 */
private fun resolveHazard(
    lookalikes: List<SpeciesLookalike>,
    certainty: Certainty,
    disputed: Boolean,
): TreeHazard {
    val lethalLookalike = lookalikes.firstOrNull { it.severity == LookalikeSeverity.LETHAL }
    if (lethalLookalike != null && (certainty != Certainty.CERTAIN || disputed)) {
        return TreeHazard.LookalikeWarning(lookalikes)
    }
    if (lookalikes.isNotEmpty()) {
        return TreeHazard.LookalikeInfo(lookalikes)
    }
    return TreeHazard.None
}

private fun resolvePreparationLabel(harvests: List<HarvestWindow>): String? {
    val hazardousHarvest = harvests.firstOrNull { it.rawEdibility != RawEdibility.EDIBLE_RAW }
    if (hazardousHarvest != null) {
        return hazardousHarvest.notes?.takeIf { it.isNotBlank() }
            ?: when (hazardousHarvest.rawEdibility) {
                RawEdibility.TOXIC_RAW -> "Toxique cru, cuisson obligatoire"
                RawEdibility.REQUIRES_PREPARATION -> "À préparer avant consommation"
                RawEdibility.EDIBLE_RAW -> null // inatteignable : filtré par firstOrNull ci-dessus
            }
    }
    // #499 : une partie EDIBLE_RAW n'a pas de libellé par défaut (rien à préparer), mais peut
    // quand même porter une note éditoriale (ex. blettissement du néflier) qui restait invisible
    // faute de partie hasardeuse pour la porter.
    return harvests.firstNotNullOfOrNull { it.notes?.takeIf { note -> note.isNotBlank() } }
}

/**
 * Libellé de saison affiché sous la frise : "Jusqu'à fin <mois>" si une fenêtre est en cours
 * (la plus éloignée de sa fin si plusieurs se chevauchent), sinon la période de la prochaine
 * fenêtre à venir (la plus proche en mois, wrap compris). Null si aucune fenêtre.
 */
private fun seasonLabel(
    harvests: List<HarvestWindow>,
    currentMonth: Int,
): String? {
    if (harvests.isEmpty()) return null

    val ongoing = harvests.filter { it.isInSeason(currentMonth) }
    if (ongoing.isNotEmpty()) {
        val furthest = ongoing.maxBy { monthsForwardWrap(currentMonth, it.monthEnd) }
        return untilEndOfLabel(furthest.monthEnd)
    }

    val next = harvests.minBy { monthsForwardWrap(currentMonth, it.monthStart) }
    return harvestPeriodLabel(next.monthStart, next.monthEnd)
}

/** Nombre de mois (0..11) pour aller de [from] à [to] en avançant, avec wrap décembre→janvier. */
private fun monthsForwardWrap(
    from: Int,
    to: Int,
): Int = ((to - from) + 12) % 12
